import * as THREE from 'three';
import { fbm, random, softDisc } from '../look/noise.js';
import type { ConeGrid } from './volcano.js';

/**
 * The volcano erupting (ADR-032, "World features and states"): lava thrown up
 * above the rim, glowing streams running down the flank towards the pupil,
 * and -- in island.ts -- a dark ash column lit orange from below. Built to be
 * seen from the shore, 260 m off, and cheap: one draw call each.
 */

const GRAVITY = 9.8;

/** Lava bombs: points thrown up from the crater, cooling from yellow to dark red as they fall. */
export function lavaFountains(options: { from: THREE.Vector3; count?: number; seed?: number }): {
  points: THREE.Points;
  update(dt: number, level: number): void;
} {
  const count = options.count ?? 700;
  const next = random(options.seed ?? 29);
  const positions = new Float32Array(count * 3);
  const colours = new Float32Array(count * 3);
  const velocity = new Float32Array(count * 3);
  const age = new Float32Array(count).fill(99);
  const parked = options.from.y - 500;
  for (let i = 0; i < count; i++) {
    positions[i * 3] = options.from.x;
    positions[i * 3 + 1] = parked;
    positions[i * 3 + 2] = options.from.z;
  }
  const geometry = new THREE.BufferGeometry();
  const position = new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage);
  const colour = new THREE.BufferAttribute(colours, 3).setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('position', position);
  geometry.setAttribute('color', colour);
  const material = new THREE.PointsMaterial({
    map: softDisc(32, 1.4),
    size: 4.5,
    sizeAttenuation: true,
    vertexColors: true,
    transparent: true,
    // Blended, not added: added to a bright sky, orange comes out white.
    depthWrite: false,
    fog: true,
  });
  const points = new THREE.Points(geometry, material);
  points.name = 'volcano-fountains';
  points.frustumCulled = false;
  // In front of the ash column's glowing foot, which is drawn at the same depth.
  points.renderOrder = 1;
  points.visible = false;
  const hot = new THREE.Color(1.7, 0.85, 0.16);
  const warm = new THREE.Color(1.35, 0.32, 0.03);
  const cold = new THREE.Color(0.16, 0.04, 0.02);
  const c = new THREE.Color();
  return {
    points,
    update(dt, level) {
      points.visible = level > 0.01;
      if (!points.visible) return;
      for (let i = 0; i < count; i++) {
        const o = i * 3;
        age[i]! += dt;
        const down = positions[o + 1]! < options.from.y - 3;
        if (down || age[i]! > 9) {
          // Thrown again, as often as the eruption is strong.
          if (next() < level * dt * 3) {
            const a = next() * Math.PI * 2;
            // Most in a low, dense jet just over the rim; some flung high and wide.
            const jet = next() < 0.6;
            const spread = jet ? 0.5 + next() * 3 : 4 + next() * 10;
            // Written in place: no array per spark, no garbage per frame.
            positions[o] = options.from.x + Math.sin(a) * next() * 6;
            positions[o + 1] = options.from.y;
            positions[o + 2] = options.from.z + Math.cos(a) * next() * 6;
            velocity[o] = Math.sin(a) * spread;
            velocity[o + 1] = jet ? 18 + next() * 12 : 28 + next() * 24;
            velocity[o + 2] = Math.cos(a) * spread;
            age[i] = 0;
          } else if (!down) {
            positions[o + 1] = parked;
          }
          continue;
        }
        velocity[o + 1]! -= GRAVITY * dt;
        positions[o]! += velocity[o]! * dt;
        positions[o + 1]! += velocity[o + 1]! * dt;
        positions[o + 2]! += velocity[o + 2]! * dt;
        // White-hot as it leaves, orange as it falls, dark only near the ground.
        const t = Math.min(1, age[i]! / 7);
        if (t < 0.55) c.copy(hot).lerp(warm, t / 0.55);
        else c.copy(warm).lerp(cold, (t - 0.55) / 0.45);
        colours[o] = c.r * level;
        colours[o + 1] = c.g * level;
        colours[o + 2] = c.b * level;
      }
      position.needsUpdate = true;
      colour.needsUpdate = true;
    },
  };
}

const streamVertex = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const streamFragment = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform float uTime;
uniform float uProgress;
uniform float uLevel;
uniform sampler2D uNoise;
varying vec2 vUv;
void main() {
  // Ragged edges and a ragged front: the flow is not a painted stripe.
  float edge = min(vUv.x, 1.0 - vUv.x) * 2.0;
  float rag = texture2D(uNoise, vec2(vUv.x * 0.7, vUv.y * 3.0)).r;
  if (edge < 0.25 + 0.5 * rag || vUv.y > uProgress - 0.04 * rag) discard;
  float flow = texture2D(uNoise, vec2(vUv.x * 1.3, vUv.y * 5.0 - uTime * 0.12)).r;
  float heat = smoothstep(0.35, 0.8, flow * 0.8 + (1.0 - vUv.y) * 0.35 + edge * 0.25);
  // The front runs hottest.
  heat = max(heat, smoothstep(0.08, 0.0, uProgress - vUv.y) * 0.9);
  vec3 crust = vec3(0.1, 0.025, 0.012);
  vec3 hot = mix(vec3(0.95, 0.13, 0.01), vec3(1.5, 0.55, 0.07), heat * heat);
  gl_FragColor = vec4(mix(crust, hot, heat) * mix(0.35, 1.0, uLevel), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  // Glowing lava shines through the haze: fogged at a third of the rest.
  #ifdef FOG_EXP2
    float fogFactor = (1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth)) * 0.35;
    gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, fogFactor);
  #else
    #include <fog_fragment>
  #endif
}
`;

function streamNoise(size = 64): THREE.DataTexture {
  const noise = fbm(31, 4, 8);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const v = Math.round(noise((x / size) * 8, (y / size) * 8) * 255);
      data.set([v, v, v, 255], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Lava streams down the flank, as ribbons laid on the cone just proud of it,
 * from the rim's crest down to about 12 m above the sea, wandering a little.
 * `towards` is the angle round the cone (volcano.ts's a) that faces the pupil.
 */
export function lavaStreams(options: {
  grid: ConeGrid;
  normals: THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  towards: number;
  seed?: number;
}): {
  mesh: THREE.Mesh;
  update(dt: number, elapsed: number, level: number): void;
  reset(full: boolean): void;
} {
  const { grid, normals } = options;
  const next = random(options.seed ?? 51);
  const segments = grid.columns - 1;
  const wrap = (j: number): number => ((j % segments) + segments) % segments;
  const point = (k: number, column: number, into: THREE.Vector3): THREE.Vector3 => {
    const i = k * grid.columns + column;
    return into.set(grid.positions[i * 3]!, grid.positions[i * 3 + 1]!, grid.positions[i * 3 + 2]);
  };
  const normal = (k: number, column: number, into: THREE.Vector3): THREE.Vector3 => {
    const i = k * grid.columns + column;
    return into.set(normals.getX(i), normals.getY(i), normals.getZ(i));
  };
  // Between two columns, so a stream wanders smoothly rather than in steps.
  const between = (
    read: typeof point,
  ): ((k: number, j: number, into: THREE.Vector3) => THREE.Vector3) => {
    const other = new THREE.Vector3();
    return (k, j, into) => {
      const left = Math.floor(wrap(j));
      const t = wrap(j) - left;
      read(k, left, into);
      return into.lerp(read(k, (left + 1) % segments, other), t);
    };
  };
  const at = between(point);
  const normalAt = between(normal);
  // The crest: the highest row; streams run from it down the outside.
  let crest = 0;
  for (let k = 0; k < grid.rows + 1; k++) {
    if (at(k, 0, new THREE.Vector3()).y > at(crest, 0, new THREE.Vector3()).y) crest = k;
  }
  const positions: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  const centreColumn = (options.towards / (Math.PI * 2)) * segments;
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (const offset of [-5.5, -1.8, 2.2, 6]) {
    const phase = next() * 10;
    const rows: number[] = [];
    for (let k = crest - 1; k > 0 && at(k, centreColumn, p).y > 12; k--) rows.push(k);
    rows.forEach((k, r) => {
      // From close together at the rim, fanning out as they run down.
      const down = r / rows.length;
      const wander = (0.2 + 0.9 * down) * Math.sin(r * 0.3 + phase);
      const j = centreColumn + offset * (0.3 + 0.7 * down) + wander;
      at(k, j, p);
      normalAt(k, j, n);
      p.addScaledVector(n.normalize(), 0.9);
      // Across the flank: round the cone, level.
      const side = new THREE.Vector3(-p.z, 0, p.x).normalize();
      const width = 2.5 + 5.5 * down;
      const base = positions.length / 3;
      positions.push(
        p.x - side.x * width,
        p.y,
        p.z - side.z * width,
        p.x + side.x * width,
        p.y,
        p.z + side.z * width,
      );
      const v = r / Math.max(1, rows.length - 1);
      uvs.push(0, v, 1, v);
      if (r > 0) index.push(base - 2, base, base - 1, base - 1, base, base + 1);
    });
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(index);
  const material = new THREE.ShaderMaterial({
    vertexShader: streamVertex,
    fragmentShader: streamFragment,
    fog: true,
    side: THREE.DoubleSide,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        uTime: { value: 0 },
        uProgress: { value: 0 },
        uLevel: { value: 0 },
        uNoise: { value: null },
      },
    ]),
  });
  material.uniforms['uNoise']!.value = streamNoise();
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'volcano-streams';
  mesh.visible = false;
  let progress = 0;
  return {
    mesh,
    update(dt, elapsed, level) {
      // The flows advance while it erupts, over about 14 s, and cool when it stops.
      if (level > 0.3) progress = Math.min(1.02, progress + dt / 14);
      mesh.visible = progress > 0.01 && level > 0.02;
      material.uniforms['uTime']!.value = elapsed;
      material.uniforms['uProgress']!.value = progress;
      material.uniforms['uLevel']!.value = level;
    },
    reset(full) {
      progress = full ? 1.02 : 0;
    },
  };
}
